import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Preloader from "../components/Preloader";
import ScrollToTop from "../components/ScrollToTop";
import MagnetCursor from "../components/MagnetCursor";

const INIT_API = "https://backenddev.aaerlaw.com/api/user/paraclearth_consultations/initialize";
const VERIFY_API = "https://backenddev.aaerlaw.com/api/user/paraclearth_consultations/verify";

const SERVICES = [
  "Corporate & Commercial Law",
  "Mergers & Acquisitions",
  "Real Estate & Property Law",
  "Employment & Labour Law",
  "Dispute Resolution & Litigation",
  "Banking & Finance",
  "Energy & Natural Resources",
  "Intellectual Property",
  "Immigration & Citizenship",
  "Tax & Regulatory Advisory",
  "Other",
];

const CONSULTATION_FEE_DISPLAY = "₦50,000";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service: "",
  preferred_date: "",
  message: "",
};

function BookConsultation() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle"); // idle | submitting | paying | verifying | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [caseNumber, setCaseNumber] = useState("");

  useEffect(() => {
    const preloader = document.getElementById("preloader");
    if (preloader) preloader.style.display = "none";

    // Load Paystack inline script if not already present
    if (!document.getElementById("paystack-inline-js")) {
      const script = document.createElement("script");
      script.id = "paystack-inline-js";
      script.src = "https://js.paystack.co/v2/inline.js";
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");

    // Step 1: Initialize booking on backend — get reference + public key
    let data;
    try {
      const res = await fetch(INIT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(form),
      });

      let responseText;
      try {
        responseText = await res.text();
        data = JSON.parse(responseText);
      } catch {
        console.error("Non-JSON response from server:", responseText);
        setErrorMsg("Server returned an unexpected response. Please try again.");
        setStatus("error");
        return;
      }

      if (!res.ok) {
        setErrorMsg(data?.message || "Could not initialize booking. Please try again.");
        setStatus("error");
        return;
      }
    } catch (err) {
      console.error("Network error during booking initialization:", err);
      setErrorMsg("Could not reach the server. Please check your connection and try again.");
      setStatus("error");
      return;
    }

    const { reference, amount, currency, email, public_key, case_number } = data;
    setCaseNumber(case_number || "");

    setStatus("paying");

    // Step 2: Open Paystack inline popup
    if (!window.PaystackPop) {
      setErrorMsg("Payment provider failed to load. Please refresh the page and try again.");
      setStatus("error");
      return;
    }

    try {
      const popup = new window.PaystackPop();
      popup.newTransaction({
        key: public_key,
        email,
        amount,         // in kobo
        currency,
        reference,
        metadata: {
          custom_fields: [
            { display_name: "Full Name", variable_name: "name", value: form.name },
            { display_name: "Phone", variable_name: "phone", value: form.phone },
            { display_name: "Service", variable_name: "service", value: form.service },
          ],
        },
        onSuccess: async function (response) {
          // Step 3: Verify payment on backend
          setStatus("verifying");
          try {
            const verifyRes = await fetch(`${VERIFY_API}/${response.reference}`);
            const verifyData = await verifyRes.json();
            if (verifyRes.ok) {
              setStatus("success");
              setForm(initialForm);
            } else {
              setErrorMsg(verifyData?.message || "Payment verification failed. Contact support with your reference: " + response.reference);
              setStatus("error");
            }
          } catch {
            setErrorMsg("Payment received but verification failed. Please contact support with reference: " + response.reference);
            setStatus("error");
          }
        },
        onCancel: function () {
          setStatus((prev) => (prev === "paying" ? "idle" : prev));
        },
        onError: function (error) {
          console.error("Paystack transaction error:", error);
          setErrorMsg(error?.message || "Could not start payment. Please try again.");
          setStatus("error");
        },
      });
    } catch (err) {
      console.error("Paystack popup error:", err);
      setErrorMsg("Could not open payment window. Please refresh the page and try again.");
      setStatus("error");
    }
  };

  const isLoading = status === "submitting" || status === "paying" || status === "verifying";

  return (
    <div>
      <Preloader />

      {/* Breadcrumb */}
      <div
        className="breadcrumb-section breadcrumb-compact bg-img jarallax"
        data-jarallax=""
        data-speed="0.6"
        style={{ backgroundImage: "url('assets/img/bg-img/73.jpg')" }}
      >
        <div className="divider"></div>
        <div className="container">
          <div className="breadcrumb-content">
            <h2
              className="wow fadeInUp"
              data-wow-duration="1000ms"
              data-wow-delay="700ms"
            >
              Book a Consultation
            </h2>
            <ul
              className="list-unstyled wow fadeInUp"
              data-wow-duration="1000ms"
              data-wow-delay="1000ms"
            >
              <li>
                <Link className="magnet-link" to="/">
                  Home
                </Link>
              </li>
              <li>Book Consultation</li>
            </ul>
          </div>
        </div>
        <div className="divider"></div>
      </div>

      {/* Booking Section */}
      <div className="contact-section bg-white">
        <div className="divider"></div>

        <div className="container">
          <div className="row g-5 justify-content-center">
            {/* Info Column */}
            <div className="col-12 col-lg-5">
              <div className="section-heading mb-5">
                <div className="sub-title">
                  <img src="assets/img/core-img/hammer.png" alt="" />
                  Consultation Booking
                </div>
                <h2>Schedule a Legal Consultation</h2>
                <p className="mt-3">
                  Speak directly with one of our legal experts. Fill in your
                  details, select your area of interest and complete a secure
                  payment to confirm your session.
                </p>
              </div>

              {/* Fee Card */}
              <div
                className="p-4 mb-4"
                style={{
                  background: "#faf6f3",
                  borderRadius: "12px",
                  borderLeft: "4px solid var(--bs-primary, #c8a96e)",
                }}
              >
                <div className="h5 fw-semibold mb-1">Consultation Fee</div>
                <div className="display-6 fw-bold mb-2">{CONSULTATION_FEE_DISPLAY}</div>
                <p className="mb-0" style={{ fontSize: "0.9rem", color: "#666" }}>
                  Includes a 60-minute session with a senior legal practitioner.
                  Secure online payment powered by Paystack.
                </p>
              </div>

              <ul className="list-unstyled" style={{ lineHeight: "2" }}>
                <li>
                  <i className="ti ti-check me-2" style={{ color: "var(--bs-primary, #c8a96e)" }}></i>
                  Immediate booking confirmation
                </li>
                <li>
                  <i className="ti ti-check me-2" style={{ color: "var(--bs-primary, #c8a96e)" }}></i>
                  Choose your preferred date &amp; practice area
                </li>
                <li>
                  <i className="ti ti-check me-2" style={{ color: "var(--bs-primary, #c8a96e)" }}></i>
                  Virtual or in-person sessions available
                </li>
                <li>
                  <i className="ti ti-check me-2" style={{ color: "var(--bs-primary, #c8a96e)" }}></i>
                  Strict confidentiality assured
                </li>
              </ul>
            </div>

            {/* Form Column */}
            <div className="col-12 col-lg-7">
              {status === "success" ? (
                <div className="text-center py-5">
                  <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>✅</div>
                  <div className="h3 fw-semibold mb-3">Booking Confirmed!</div>
                  <p className="mb-4">
                    Thank you. Your consultation has been booked and payment
                    received. Our team will contact you shortly to confirm the
                    session details.
                  </p>
                  {caseNumber && (
                    <p className="mb-4">
                      Your matter reference is <strong>{caseNumber}</strong>.
                    </p>
                  )}
                  <Link to="/" className="btn btn-primary">
                    Back to Home <i className="ti ti-arrow-up-right"></i>
                  </Link>
                </div>
              ) : (
                <div className="contact-form bg-primary p-4 p-lg-5" style={{ borderRadius: "12px" }}>
                  <div className="mb-4 contact-title">Your Booking Details</div>

                  {status === "error" && (
                    <div
                      className="alert mb-4"
                      style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: "8px", padding: "12px 16px" }}
                    >
                      {errorMsg}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="row g-4">
                      <div className="col-12 col-lg-6">
                        <input
                          type="text"
                          name="name"
                          className="form-control"
                          placeholder="Full Name *"
                          value={form.name}
                          onChange={handleChange}
                          required
                          disabled={isLoading}
                          autoComplete="name"
                        />
                      </div>

                      <div className="col-12 col-lg-6">
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          placeholder="Email Address *"
                          value={form.email}
                          onChange={handleChange}
                          required
                          disabled={isLoading}
                          autoComplete="email"
                        />
                      </div>

                      <div className="col-12 col-lg-6">
                        <input
                          type="tel"
                          name="phone"
                          className="form-control"
                          placeholder="Phone Number"
                          value={form.phone}
                          onChange={handleChange}
                          disabled={isLoading}
                          autoComplete="tel"
                        />
                      </div>

                      <div className="col-12 col-lg-6">
                        <input
                          type="date"
                          name="preferred_date"
                          className="form-control"
                          value={form.preferred_date}
                          onChange={handleChange}
                          disabled={isLoading}
                          min={new Date().toISOString().split("T")[0]}
                        />
                      </div>

                      <div className="col-12">
                        <select
                          name="service"
                          className="form-control"
                          value={form.service}
                          onChange={handleChange}
                          disabled={isLoading}
                        >
                          <option value="">Select Practice Area</option>
                          {SERVICES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-12">
                        <textarea
                          name="message"
                          className="form-control"
                          placeholder="Brief description of your legal matter"
                          rows="5"
                          value={form.message}
                          onChange={handleChange}
                          disabled={isLoading}
                        ></textarea>
                      </div>

                      <div className="col-12">
                        <p className="consultation-fee-notice mb-3">
                          By proceeding you agree to a consultation fee of{" "}
                          <strong>{CONSULTATION_FEE_DISPLAY}</strong>. Payment is
                          processed securely via Paystack.
                        </p>
                        <button
                          type="submit"
                          className="btn btn-dark w-100"
                          disabled={isLoading}
                        >
                          {status === "submitting" && (
                            <span>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>
                              Preparing payment&hellip;
                            </span>
                          )}
                          {status === "paying" && (
                            <span>Complete payment in the popup&hellip;</span>
                          )}
                          {status === "verifying" && (
                            <span>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>
                              Verifying payment&hellip;
                            </span>
                          )}
                          {(status === "idle" || status === "error") && (
                            <>Pay {CONSULTATION_FEE_DISPLAY} &amp; Book{" "}
                              <i className="ti ti-arrow-up-right"></i>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="divider"></div>
      </div>

      <ScrollToTop />
      <MagnetCursor />
    </div>
  );
}

export default BookConsultation;
